import { Variant } from '@/@types/shopify';

type PackOptionsConfig = {
  wrapper: HTMLElement;
  onInit?: (instance: PackOptions) => void;
  onSelect?: (instance: PackOptions, multiple: number) => void;
};

export default class PackOptions {
  private wrapper: HTMLElement;
  private productWrapper: HTMLElement;
  private onInit?: (instance: PackOptions) => void;
  private onSelect?: (instance: PackOptions, multiple: number) => void;
  private selectedMultiple: number;

  constructor(config: PackOptionsConfig) {
    if (!config.wrapper) {
      throw new Error('No wrapper found');
    }

    this.wrapper = config.wrapper;
    this.productWrapper = document.querySelector('.Product') as HTMLElement;
    this.onInit = config.onInit;
    this.onSelect = config.onSelect;

    // If the default pack is not available due to inventory, select the first available pack
    if (!this.wrapper.querySelector('[data-bundle-option].active')) {
      this.wrapper.querySelector('[data-bundle-option]:not([disabled])')?.classList.add('active');
    }

    this.selectedMultiple = parseInt(
      this.wrapper
        .querySelector('[data-bundle-option].active')
        ?.getAttribute('data-pack-multiple') || '1',
    );

    this.onInit?.(this);
  }

  private handleClick(event: Event) {
    event.preventDefault();

    const packOption = (event.target as HTMLElement).closest(
      '[data-bundle-trigger]',
    ) as HTMLElement;

    if (!packOption) {
      console.error('No pack option found');
      return;
    }

    if (packOption.closest('.ProductForm__OptionWrapper')!.hasAttribute('disabled')) {
      console.error('Pack option is disabled');
      return;
    }

    const optionWrapper = packOption.closest(
      '[data-bundle-option][data-pack-multiple]',
    ) as HTMLElement;

    if (!optionWrapper) {
      console.error('No option wrapper found', packOption);
      return;
    }

    let multiple = parseInt(optionWrapper.getAttribute('data-pack-multiple') || '1');

    if (isNaN(multiple)) {
      console.error('No multiple found', packOption);
      multiple = 1;
    }

    [...this.wrapper.querySelectorAll<HTMLElement>('[data-bundle-option]')].forEach((option) => {
      option.classList.remove('active');
    });
    packOption.closest('[data-bundle-option]')!.classList.add('active');

    this.selectedMultiple = multiple;
    this.onSelect?.(this, multiple);
  }

  private selectFirstAvailableOption() {
    this.wrapper.querySelector('[data-bundle-option]:not([disabled])')?.classList.add('active');
  }

  /**
   * Hides pack options when a variant does not meet eligibility requirements
   */
  hidePackOptions() {
    this.wrapper.classList.add('hidden');
  }

  /**
   * Shows pack options when a variant meets eligibility requirements
   */
  showPackOptions() {
    this.wrapper.classList.remove('hidden');
  }

  private updateSelectedOption() {
    this.wrapper.querySelectorAll<HTMLElement>('[data-bundle-option]').forEach((option) => {
      option.classList.remove('active');
    });

    const defaultOption = this.wrapper.querySelector('[data-default]') as HTMLElement;

    if (!defaultOption) {
      this.selectFirstAvailableOption();
      return;
    }

    const defaultOptionDisabled = defaultOption.hasAttribute('disabled');

    if (!defaultOptionDisabled) {
      defaultOption.classList.add('active');
      return;
    }

    this.selectFirstAvailableOption();

    this.selectedMultiple = parseInt(
      this.wrapper
        .querySelector('[data-bundle-option].active')
        ?.getAttribute('data-pack-multiple') || '1',
    );

    this.onSelect?.(this, this.selectedMultiple);
  }

  bindEvents() {
    const packOptions = this.wrapper.querySelectorAll('[data-bundle-trigger]');

    packOptions.forEach((packOption) => {
      packOption.addEventListener('click', (e) => this.handleClick(e));
    });

    this.productWrapper.addEventListener('variant:changed', (e: Event) => {
      const variant = (e as CustomEvent).detail.variant as Variant;

      const inventoryQuantity = window?.PDP?.variants.find(
        (v: Variant) => v.id === variant.id,
      )?.inventory_quantity;

      if (typeof inventoryQuantity !== 'number') {
        console.error('No inventory quantity found');
        return;
      }

      packOptions.forEach((packOption) => {
        const optionWrapper = packOption.closest(
          '[data-bundle-option][data-pack-multiple]',
        ) as HTMLElement;
        const multiple = parseInt(optionWrapper.getAttribute('data-pack-multiple') || '1');

        if (inventoryQuantity >= multiple) optionWrapper.removeAttribute('disabled');
        else optionWrapper.setAttribute('disabled', 'disabled');
      });

      this.updateSelectedOption();
    });
  }

  getActivePackMultiple() {
    return this.selectedMultiple;
  }

  setActivePackMultiple(multiple: number) {
    this.selectedMultiple = multiple;
    this.wrapper.querySelector('[data-bundle-option].active')?.classList.remove('active');
    this.wrapper.querySelector(`[data-pack-multiple="${multiple}"]`)?.classList.add('active');
    this.onSelect?.(this, multiple);
  }
}
